import { analyzeLessonConflicts } from '@/constants';
import React, { useCallback, useMemo } from 'react';
import {
  Lesson,
  DailyRosterInstructor
} from '@/constants/Interfaces/CommonInterfaces';
import HoverableLessonCell from './HoverableLessonCell';
import { setModalConfig } from '@/stores/common';
import useDailyRosterStore from '@/stores/dailyRosterStore';
import useInstructorRosterStore from '@/stores/instructorRosterStore';
import { convertToRgba } from '@/utils/common';
import { isMobile, isTablet } from 'react-device-detect';

interface RowContentProps {
  instructor: DailyRosterInstructor;
  rowDate?: string; // explicit date for instructor-month table rows
  onLessonClick?: (
    lesson: Lesson,
    instructor: DailyRosterInstructor | null,
    slot: string
  ) => void;
  type?: 'daily' | 'instructor';
  columnWidths?: number[];
}
type UILesson = Lesson & {
  id: number;
  slotIndex?: number | null;
  schedule_group?: { id: number };
  number_in_party?: number;
};

const RowContent: React.FC<RowContentProps> = ({
  instructor,
  rowDate,
  onLessonClick: _onLessonClick,
  type,
  columnWidths = []
}) => {
  const { selectedDate: dailySelectedDate } = useDailyRosterStore();
  const { selectedDate: instructorSelectedDate, selectedInstructor } =
    useInstructorRosterStore();
  const selectedDate =
    type === 'daily' ? dailySelectedDate : (rowDate ?? instructorSelectedDate);
  const onSlotOptionClick = useCallback(
    (slot: string, lessonId: number | null, lesson?: UILesson | null) => {
      setModalConfig({
        visible: true,
        type: 'scheduleLessonModal',
        props: {
          size: 'rem90'
        },
        data: {
          title: lessonId ? 'Update Lesson' : 'Add Lesson',
          lessonId: lessonId ?? null,
          date: selectedDate,
          slot: slot,
          instructor: instructor,
          instructor_id:
            selectedInstructor ??
            (instructor as unknown as { id?: number })?.id ??
            null,
          lesson: lesson ?? undefined
        }
      });
    },
    [instructor, selectedDate, selectedInstructor]
  );
  // Get content for a specific slot index
  const getContentForSlot = useCallback((lesson: UILesson) => {
    switch (lesson?.slotIndex) {
      case 1: {
        const label =
          `${lesson.string && Number(lesson.string) > 0 ? Number(lesson.string) : ''} ${lesson.client_name ?? ''}`.trim();
        return label || 'N/A';
      }
      case 2:
        return lesson.lesson_req || 'NA';
      case 3:
        return lesson.lesson_code?.code ? `${lesson.lesson_code?.code}` : 'N/A';
      case 4: {
        const cam = lesson.am ?? 0;
        const cfd = lesson.fd ?? 0;
        const cpm = lesson.pm ?? 0;
        return `${cam} + ${cpm} + ${cfd}`;
      }
      case 5:
        return (
          lesson.transport?.name ||
          lesson.transports?.name ||
          lesson.car?.name ||
          lesson.vehicle?.name ||
          'N/A'
        );
      case 6:
        return lesson.ski_level || 'N/A';
      default:
        return '';
    }
  }, []);

  // Memoized row generation
  const rowCells = useMemo(() => {
    const isUnavailable = instructor?.day_off ?? false;
    const analysisInput: UILesson[] = (instructor?.lessons || [])?.map(
      (l, i) => ({
        id: i + 1,
        ...l
      })
    );
    const slotData = analyzeLessonConflicts(analysisInput);
    const { results } = slotData || {};
    return results?.map((slotData, index) => {
      const { conflictCount = 0, lessons = [], slot } = slotData;
      const backgroundColor =
        lessons?.length && lessons[lessons.length - 1]?.lesson_code?.color
          ? lessons[lessons.length - 1]?.lesson_code?.color
          : isUnavailable
            ? '#EF4444'
            : '#fff';
      const opacity =
        lessons?.length && lessons[lessons.length - 1]?.lessons_entry === 'Y'
          ? 0.7
          : 1;

      const columnWidth = columnWidths[index] || 45;
      return (
        <td
          key={index}
          className={`border relative h-${8 * conflictCount}`} // Added fixed height for consistency
          style={{
            backgroundColor: convertToRgba(backgroundColor, opacity),
            minWidth: `${columnWidth}px`,
            maxWidth: '400px',
            width: `${columnWidth}px`
            // opacity: opacity
          }}
        >
          {lessons?.length > 0 ? (
            lessons?.map((lesson, index) => {
              const hasConflict = lessons.length > 1;
              const uiLesson = lesson as UILesson;
              const isReqYellow =
                uiLesson?.slotIndex === 2 &&
                uiLesson?.lesson_req &&
                uiLesson?.lesson_req !== 'NA';
              const isVehicleYellow =
                uiLesson?.slotIndex === 5 &&
                (uiLesson?.transport?.name ||
                  uiLesson?.transports?.name ||
                  uiLesson?.car?.name ||
                  uiLesson?.vehicle?.name);
              const isLevelYellow = uiLesson?.slotIndex === 6;

              const textColor =
                isReqYellow || isVehicleYellow || isLevelYellow
                  ? '#fdff37'
                  : '#fff';

              return (
                <button
                  type='button'
                  key={index}
                  style={{
                    height: `${100 / lessons.length}%`, // Divide height equally
                    top: `${(100 / lessons.length) * index}%`, // Position each div
                    color: textColor
                  }}
                  onClick={() =>
                    onSlotOptionClick?.(
                      slot,
                      (lesson as UILesson)?.schedule_group?.id ?? null,
                      lesson as UILesson
                    )
                  }
                  className='absolute border-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 p-0 m-0 text-center left-0 w-full flex items-center justify-center text-white text-xs truncate px-1'
                >
                  {(lesson as UILesson)?.slotIndex === 1 &&
                  !isMobile &&
                  !isTablet ? (
                    <HoverableLessonCell
                      lesson={lesson as UILesson}
                      instructor={instructor}
                      content={getContentForSlot(lesson as UILesson)}
                      onClick={() =>
                        onSlotOptionClick?.(
                          slot,
                          (lesson as UILesson)?.schedule_group?.id ?? null,
                          lesson as UILesson
                        )
                      }
                      hasConflict={hasConflict}
                      className={hasConflict ? 'h-full' : ''}
                    />
                  ) : (
                    getContentForSlot(lesson as UILesson)
                  )}
                </button>
              );
            })
          ) : (
            <button
              type='button'
              style={{ backgroundColor: isUnavailable ? '#EF4444' : '#fff' }}
              className='absolute top-0 left-0 w-full h-full flex items-center justify-center text-xs border-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 p-0 m-0'
              // onClick={() => onLessonClick?.(null, null, slot)}
              onClick={() => onSlotOptionClick(slot, null)}
              aria-label={`Add lesson for ${instructor?.name ?? 'instructor'} at ${slot}`}
            ></button>
          )}
        </td>
      );
    });
  }, [instructor, getContentForSlot, onSlotOptionClick, columnWidths]);

  return <>{rowCells}</>;
};

export default RowContent;
